export const PHONE = "+1-866-488-9356";
export const NOLA_PHONE = "+1-888-969-8372";

/**
 * Safely serialize data for embedding inside a <script type="application/ld+json">
 * tag. JSON.stringify does not escape `<`, `>`, `&`, or `/`, so a string like
 * `</script><script>...</script>` could break out of the script context and
 * execute attacker-controlled JavaScript. Replace those characters with their
 * unicode escapes so the JSON remains valid but cannot terminate the tag.
 */
export function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function postalAddress(street: string, city: string, region: string) {
  return {
    "@type": "PostalAddress",
    streetAddress: street,
    addressLocality: city,
    addressRegion: region,
    addressCountry: "US",
  };
}

export function locationSchema(
  name: string,
  street: string,
  city: string,
  region: string,
  phone: string,
  pageUrl: string,
  description: string,
) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "LocalBusiness"],
    name: `RESTORE Hair — ${name}`,
    description,
    url: pageUrl,
    telephone: phone,
    address: postalAddress(street, city, region),
  };
}

export function getSouthLocations(url: string, description: string) {
  return [
    locationSchema("Boca Raton", "2255 Glades Road, Suite 324A", "Boca Raton", "FL", PHONE, url, description),
    locationSchema("Jacksonville", "4651 Salisbury Rd, Suite 400", "Jacksonville", "FL", PHONE, url, description),
    locationSchema("Miami", "80 SW 8th St, Suite 2000", "Miami", "FL", PHONE, url, description),
    locationSchema("Tampa", "8875 Hidden River Pkwy, Suite 300", "Tampa", "FL", PHONE, url, description),
    locationSchema("Atlanta", "1201 Peachtree Street, Suite 200", "Atlanta", "GA", PHONE, url, description),
    locationSchema("New Orleans", "1615 Poydras St, Suite 900", "New Orleans", "LA", NOLA_PHONE, url, description),
    locationSchema("Charlotte", "7725 Ballantyne Commons Parkway, Suite 101", "Charlotte", "NC", PHONE, url, description),
    locationSchema("Cornelius", "19109 West Catawba Ave, Suite 200", "Cornelius", "NC", PHONE, url, description),
    locationSchema("Raleigh", "4509 Creedmoor Rd, Suite 201", "Raleigh", "NC", PHONE, url, description),
    locationSchema("Oklahoma City", "101 Park Ave, Suite 1300", "Oklahoma City", "OK", PHONE, url, description),
    locationSchema("Nashville", "3200 West End Ave, Suite 500", "Nashville", "TN", PHONE, url, description),
    locationSchema("Allen", "825 Watters Creek Blvd, Building M Suite 250", "Allen", "TX", PHONE, url, description),
    locationSchema("Arlington", "3901 Arlington Highlands Blvd, Suite 200", "Arlington", "TX", PHONE, url, description),
    locationSchema("Austin", "300 Beardsley Lane, Bldg C, Suite 101", "Austin", "TX", PHONE, url, description),
    locationSchema("Dallas – Highland Park", "4514 Cole Avenue, Office #605", "Dallas", "TX", PHONE, url, description),
    locationSchema("Dallas – Plano", "5425 W Spring Creek, Suite 170", "Plano", "TX", PHONE, url, description),
    locationSchema("Fort Worth", "1751 River Run, Suite 200", "Fort Worth", "TX", PHONE, url, description),
    locationSchema("Houston", "2929 Allen Pkwy, Suite 200", "Houston", "TX", NOLA_PHONE, url, description),
    locationSchema("San Antonio", "700 N St Mary's St, Suite 1400", "San Antonio", "TX", NOLA_PHONE, url, description),
    locationSchema("Southlake", "950 E State Hwy 114", "Southlake", "TX", PHONE, url, description),
    locationSchema("Virginia Beach", "780 Lynnhaven Pkwy, Suite 400", "Virginia Beach", "VA", PHONE, url, description),
  ];
}

export const SITE_URL = "https://restorehair.com";

/** Asset pointers are site-relative; schema.org needs absolute URLs. */
export function absUrl(url: string) {
  return url.startsWith("http") ? url : `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

/** Sitewide publisher identity, emitted once from the root route. */
export function organizationSchema(logoUrl: string) {
  return {
    "@context": "https://schema.org",
    "@type": ["MedicalOrganization", "Organization"],
    "@id": `${SITE_URL}/#organization`,
    name: "RESTORE Hair",
    alternateName: "RESTORE Hair Transplants",
    url: SITE_URL,
    logo: absUrl(logoUrl),
    image: absUrl(logoUrl),
    telephone: PHONE,
    medicalSpecialty: "Dermatology",
    sameAs: ["https://www.instagram.com/restore_hair/"],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        contactType: "customer service",
        areaServed: "US",
        availableLanguage: "English",
      },
    ],
  };
}

export function physicianSchema(opts: {
  name: string;
  jobTitle: string;
  description: string;
  image: string;
  pageUrl: string;
  /** The clinic where the doctor actually practices. Defaults to Denver HQ. */
  clinic?: { street: string; city: string; state: string };
}) {
  const clinic = opts.clinic ?? {
    street: "5445 DTC Pkwy, Suite 1015",
    city: "Greenwood Village",
    state: "CO",
  };
  // Physician is a LocalBusiness subtype, so validators require an address,
  // and referenced organizations must be self-describing (name + url).
  const org = {
    "@type": "MedicalOrganization",
    "@id": `${SITE_URL}/#organization`,
    name: "RESTORE Hair",
    url: `${SITE_URL}/`,
    telephone: PHONE,
  };
  return {
    "@context": "https://schema.org",
    // Physician is an Organization/LocalBusiness type in Schema.org, so
    // person-only properties (jobTitle, worksFor) are invalid here.
    "@type": "Physician",
    name: opts.name,
    description: opts.description,
    image: absUrl(opts.image),
    url: opts.pageUrl,
    medicalSpecialty: "Dermatology",
    telephone: PHONE,
    priceRange: "$$$",
    address: postalAddress(clinic.street, clinic.city, clinic.state),
    parentOrganization: org,
    memberOf: org,
  };
}

/** Blog archive pages (category / tag / author). */
export function collectionPageSchema(name: string, description: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: { "@id": `${SITE_URL}/#organization` },
  };
}

/** Clinic list for a state hub page. */
export function stateHubSchema(
  stateName: string,
  path: string,
  cities: { name: string; address: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `RESTORE Hair locations in ${stateName}`,
    url: `${SITE_URL}${path}`,
    itemListElement: cities.map((city, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": ["MedicalBusiness", "LocalBusiness"],
        name: `RESTORE Hair — ${city.name}`,
        telephone: PHONE,
        parentOrganization: { "@id": `${SITE_URL}/#organization` },
        address: postalAddress(city.address, city.name.split("–")[0]!.trim(), stateName),
      },
    })),
  };
}
